import { useMutation, useTRPC } from "@luckymenu/api-client/react";
import type { Item, ItemTag } from "@luckymenu/core";
import { useCallback, useMemo, useState } from "react";
import { withItem, withoutItem } from "../menu/cache";
import type { FormErrors } from "../menu/form-errors";
import {
	addVariant,
	type ItemDraft,
	itemDraftToInput,
	itemToDraft,
	moveVariant,
	newItemDraft,
	removeVariant,
	setDefaultVariant,
	toggleModifierGroup,
	toggleSuggestion,
	toggleTag,
	updateVariant,
	type VariantDraft,
} from "../menu/item-form";
import { type MenuProblem, menuProblemOf } from "../menu/problems";
import { useFileUpload } from "./use-file-upload";
import { useMenu, useMenuCache } from "./use-menu";
import { useStoredDraft } from "./use-stored-draft";

/** A new item's draft is identified without its ID, which is made fresh each time. */
const newBase = (draft: ItemDraft) => JSON.stringify({ ...draft, id: "" });
const editBase = (draft: ItemDraft) => JSON.stringify(draft);

/**
 * The item form, shared by both apps: `itemId` to edit, or null with
 * `categoryId` for a new item. The draft survives restarts; photos upload
 * through `useFileUpload("item_image")`; saving and deleting update the
 * cached menu. Needs `menu.edit`.
 */
export function useItemForm({
	itemId,
	categoryId,
}: {
	itemId: string | null;
	categoryId?: string | null;
}) {
	const trpc = useTRPC();
	const menu = useMenu();
	const cache = useMenuCache();
	const photo = useFileUpload("item_image");
	const [errors, setErrors] = useState<FormErrors>({});
	const [problem, setProblem] = useState<MenuProblem | null>(null);

	const original: Item | null = useMemo(
		() => (itemId ? (menu.data?.items.find((i) => i.id === itemId) ?? null) : null),
		[menu.data, itemId],
	);
	const firstCategory = menu.data?.categories[0]?.id ?? null;
	const newCategory = categoryId ?? firstCategory;
	const initial: ItemDraft | null = useMemo(() => {
		if (itemId) return original ? itemToDraft(original) : null;
		return newCategory ? newItemDraft(newCategory) : null;
	}, [itemId, original, newCategory]);
	const stored = useStoredDraft(
		itemId ? `item:${itemId}` : `item:new:${newCategory ?? ""}`,
		initial,
		itemId ? editBase : newBase,
	);
	const draft = stored.draft;

	const edit = useCallback(
		(change: (d: ItemDraft) => ItemDraft) => {
			setProblem(null);
			stored.update(change);
		},
		[stored.update],
	);
	const clearError = (...keys: string[]) =>
		setErrors((e) => {
			if (!keys.some((k) => k in e)) return e;
			const next = { ...e };
			for (const k of keys) delete next[k];
			return next;
		});

	const save = useMutation(trpc.staff.catalog.upsertItem.mutationOptions());
	const remove = useMutation(trpc.staff.catalog.deleteItem.mutationOptions());

	return {
		/** Menu still loading, or the item is not in it. */
		status: menu.isPending
			? ("loading" as const)
			: itemId && !original
				? ("notFound" as const)
				: !draft
					? ("loading" as const)
					: ("ready" as const),
		isNew: itemId === null,
		original,
		draft,
		restored: stored.restored,
		dirty: stored.dirty,
		errors,
		problem,
		saving: save.isPending,
		deleting: remove.isPending,
		/** Categories, add-on groups, tax classes and stations to choose from. */
		menu: menu.data,

		set: <K extends keyof ItemDraft>(field: K, value: ItemDraft[K]) => {
			clearError(field);
			edit((d) => ({ ...d, [field]: value }));
		},
		addVariant: () => {
			clearError("price");
			edit(addVariant);
		},
		removeVariant: (index: number) => {
			setErrors((e) =>
				Object.fromEntries(Object.entries(e).filter(([k]) => !k.startsWith("variants."))),
			);
			edit((d) => removeVariant(d, index));
		},
		updateVariant: (index: number, change: Partial<Omit<VariantDraft, "id" | "isDefault">>) => {
			clearError(...Object.keys(change).map((k) => `variants.${index}.${k}`));
			edit((d) => updateVariant(d, index, change));
		},
		setDefaultVariant: (index: number) => edit((d) => setDefaultVariant(d, index)),
		moveVariant: (from: number, to: number) => edit((d) => moveVariant(d, from, to)),
		toggleTag: (tag: ItemTag) => edit((d) => toggleTag(d, tag)),
		toggleModifierGroup: (groupId: string) => edit((d) => toggleModifierGroup(d, groupId)),
		/** "Goes well with" (P9-T11): at most four other dishes. */
		toggleSuggestion: (itemId: string) => edit((d) => toggleSuggestion(d, itemId)),

		/** Upload state for the photo field. */
		photo: photo.state,
		/** Opens the picker and uploads; the photo is attached when the item is saved. */
		pickPhoto: async () => {
			const info = await photo.pickAndUpload();
			if (info) edit((d) => ({ ...d, image: { id: info.id, blurhash: info.blurhash } }));
		},
		removePhoto: () => {
			photo.reset();
			edit((d) => ({ ...d, image: null }));
		},
		cancelPhoto: photo.cancel,

		/** Back to the saved item, forgetting the stored draft. */
		discard: () => {
			setErrors({});
			setProblem(null);
			photo.reset();
			stored.reset();
		},

		/** Saves, or shows what to fix. Resolves to the saved item, or null. */
		submit: async (): Promise<Item | null> => {
			if (!draft) return null;
			const result = itemDraftToInput(draft, original);
			if (!result.ok) {
				setErrors(result.errors);
				return null;
			}
			setErrors({});
			setProblem(null);
			try {
				const item = await save.mutateAsync(result.input);
				cache.update((m) => withItem(m, item));
				// A new item's form moves on to the item; an edited one shows what was saved.
				if (itemId) stored.reset(itemToDraft(item));
				else stored.forget();
				void cache.refresh();
				return item;
			} catch (error) {
				const reason = menuProblemOf(error);
				if (reason === "taken") setErrors({ shortCode: "taken" });
				else if (reason === "price") setErrors({ price: "required" });
				setProblem(reason);
				if (reason === "notFound") void cache.refresh();
				return null;
			}
		},

		/** Deletes the item (after the app has confirmed). */
		delete: async (): Promise<boolean> => {
			if (!itemId) return false;
			try {
				await remove.mutateAsync({ id: itemId });
				stored.forget();
				cache.update((m) => withoutItem(m, itemId));
				return true;
			} catch (error) {
				setProblem(menuProblemOf(error));
				return false;
			}
		},
	};
}

export type ItemForm = ReturnType<typeof useItemForm>;
